import React from 'react'
import Highcharts from 'highcharts'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import useActivitiesDisplayName from '../../hooks/useDisplayNames/variants/useActivitiesDisplayName'

type GanttChartHeaderProps = {
    /** The chart's built series — bars drawn is counted off these. */
    series?: Highcharts.Options['series']
    /** Activities in scope for the chart, drawable or not. */
    totalCount: number
    /** Legend, rendered alongside the title. */
    children?: React.ReactNode
}

/**
 * Counts the bars across a chart's series. Taken off the built options rather
 * than by re-filtering activities, so the number can't drift from what the
 * chart actually drew.
 *
 * @param {Highcharts.Options['series']} series - The chart's series, if built yet.
 * @returns {number} Total bars across all series.
 */
export const countRenderedBars = (series: Highcharts.Options['series']): number =>
    (series || []).reduce((total, entry) => total + ((entry as { data?: unknown[] }).data?.length || 0), 0)

/**
 * Shared title row for the gantt charts: the "<Activity> Timing" heading, an
 * explanation of why an activity might be absent, and how many of the ones in
 * scope were drawn. Kept in one place so both charts stay worded the same.
 */
const GanttChartHeader = ({ series, totalCount, children }: GanttChartHeaderProps) => {
    const activitiesDisplayName = useActivitiesDisplayName()

    const renderedCount = React.useMemo(() => countRenderedBars(series), [series])

    return (
        <Stack
            direction="row"
            gap={2}
            mx={-2}
            px={2}
            pb={1}
            borderBottom="1px solid"
            borderColor="figma.grayscale.stroke"
            whiteSpace="nowrap"
            flexWrap="wrap"
            alignItems="center"
        >
            <Stack direction="row" alignItems="center" gap={0.5} mr={2}>
                <Typography variant="16x700" color="figma.grayscale.textHier1">
                    {activitiesDisplayName.singular} Timing
                </Typography>
                {/* Tooltip takes exactly one child, so this comment sits outside it.
                    tabIndex/role make the icon focusable so the tooltip opens on
                    keyboard focus — MUI won't open it for a bare SVG. */}
                <Tooltip
                    arrow
                    title={`${activitiesDisplayName.plural} need both a start and an end date to appear on this chart. Planned dates are used when actual dates are missing — those bars appear lighter.`}
                >
                    <InfoOutlinedIcon
                        tabIndex={0}
                        role="button"
                        aria-label="Why might an activity be missing from this chart?"
                        sx={{ fontSize: 16, color: 'text.secondary', cursor: 'default' }}
                    />
                </Tooltip>
                <Typography variant="body2" color="figma.grayscale.textHier2" ml={0.5}>
                    {renderedCount}/{totalCount} {activitiesDisplayName.plural} showing
                </Typography>
            </Stack>
            {children}
        </Stack>
    )
}

export default GanttChartHeader
